import { format } from 'd3-format';
import { GetString } from 'fluent-react';
import sortBy from 'lodash-es/sortBy';
import React, { useState } from 'react';
import styled, {css} from 'styled-components';
import {
  DecileClassification, ProductClass, ProductLevel, TradeDirection, TradeFlow,
} from '../../graphQL/types/shared';
import { formatTradeValue } from '../../numberFormatters';
import { useLink } from '../../routing';
import { RouteID } from '../../routing/routes';

import { failIfValidOrNonExhaustive, newGetDisplayedProductCode } from '../../Utils';
import {
  Target,
} from '../../Utils';
import { QueryStore } from '../../viz/queryStoreUtils';
import { convertQueryStoreIntoQueryParams } from '../../viz/routingUtils';
import {
  NodeSizing, VizType,
} from '../../viz/Utils';
import GlossaryTerm, {TooltipAlign, TooltipType} from '../landing/GlossaryTerm';
import {
  ArrowIconContainer,
} from '../topPageIndicator/Utils';
import usePromotableZIndex from '../usePromotableZIndex';
import Diamond, { Fill } from './Diamond';
import StarRating from './StarRating';
import TitleCell, {
  SortCriterion,
  SortDirection,
} from './TitleCell';

const titleCellBaseZIndex = 20;
const promotedTitleCellZIndex = 3000;

const zIndices = {
  tableCellProductName: titleCellBaseZIndex + 5,
  tableCellComplexity: titleCellBaseZIndex,
  tableCellOpportunityGain: titleCellBaseZIndex,
  tableCellFeasibility: titleCellBaseZIndex,
  tableCellExportValue: titleCellBaseZIndex,
  tableCellExportValueChange: titleCellBaseZIndex,
  dataCell: 10,
};

//#region Styling
const strokeColor = '#808080';
const strokeLightColor = '#b3b3b3';
const productTextColor = '#666';
const Root = styled.div`
  display: grid;
  grid-template-columns: 3fr repeat(3, 1.5fr) repeat(2, 1fr);
  overflow: auto;

  /* stylelint-disable-next-line property-no-vendor-prefix */
  ::-webkit-scrollbar {
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-appearance: none;
    height: 8px;
    width: 8px;
  }
  /* stylelint-disable-next-line property-no-vendor-prefix */
  ::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.3);
  }
  /* stylelint-disable-next-line property-no-vendor-prefix */
  ::-webkit-scrollbar-track {
    background-color: rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 1050px) {
    font-size: 0.8rem;
  }
`;
const cellBottomBorderStyle = css`
  border-bottom: 1px solid ${strokeLightColor};
`;

const NewTabArrow = styled.div`
  position: absolute;
  bottom: 0.4rem;
  right: 0.4rem;
  width: 0.625rem;

  svg {
    width: 100%;
    height: 100%;
    fill: ${productTextColor};
  }
`;

const DataCell = styled.div`
  ${cellBottomBorderStyle}
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: ${zIndices.dataCell};
`;
const titleColoredBarSeparationFromEdge = '0.5rem';
const ProductNameOuterContainer = styled(DataCell)`
  position: sticky;
  left: 0;
  background-color: #fff;
  z-index: 15;
  justify-content: flex-start;
  border-right: 1px solid ${strokeColor};

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: ${titleColoredBarSeparationFromEdge};
    bottom: ${titleColoredBarSeparationFromEdge};
    left: 0;
    width: 0.5rem;
    background-color: var(--colored-bar-background-color);
  }

  svg {
    display: none;
  }

  &:hover {
    background-color: #efefef;

    svg {
      display: block;
    }
  }
`;
const ExportValueChangeContainer = styled.div`
  margin-left: 0.5rem;
`;
const ProductName = styled.span`
  color: ${productTextColor};

  &::after {
    content: '';
    display: inline-block;
    margin-right: 0.5rem;
  }
`;
const ProductCode = styled.span`
  color: ${productTextColor};
  font-weight: 300;
`;
const ArrowDown = ArrowIconContainer;
const ArrowUp = styled(ArrowIconContainer)`
  transform: rotate(180deg);
`;

const ProductLink = styled.a`
  display: block;
  padding: 1rem;
  width: 100%;

  @media (max-width: 975px) {
    padding: 0.6rem 0.6rem 0.6rem 1rem;
  }
`;
const DistanceLegendContainer = styled.div`
  margin-top: 0.5rem;
`;
const DistanceLegend = styled.div`
  display: flex;
  position: relative;
  align-items: center;

  &:first-child {
    margin-bottom: 0.3rem;
  }
`;
const LegendLabel = styled.em`
  margin-left: 1rem;
  font-size: 0.9rem;

  @media (max-width: 1050px) {
    margin-left: 0.5rem;
    font-size: 0.8rem;
  }
`;
//#endregion

// Allow CSS custom properties
declare module 'csstype' {
  interface Properties {
    '--colored-bar-background-color'?: string;
  }
}

const formatChange = format('.1~%');
const TableRow = (props: {product: TableDatum, year: number}) => {
  const {product, year} = props;
  const {
    id, color, name, code,
    complexityDecile, opportunityGainDecile,
    feasibilityDecile,
    globalExportValue, globalExportValueChange,
  } = product;
  const productNameStyle: React.CSSProperties = {
    '--colored-bar-background-color': color,
  };
  const productCode = `(${newGetDisplayedProductCode(code, ProductClass.HS, ProductLevel.fourDigit)})`;
  const globalExportValueChangeMagnitude = Math.abs(globalExportValueChange);
  const arrowColor = (globalExportValueChange >= 0) ? '#f1c086' : '#8fd8e5';
  const Arrow = (globalExportValueChange >= 0) ? ArrowUp : ArrowDown;

  const productIDString = id.split('product-HS-').pop();
  let productIDNumber: number | undefined;
  if (productIDString !== undefined) {
    productIDNumber = parseInt(productIDString, 10);
  }
  const newState: QueryStore = {
    country: undefined,
    product: productIDNumber,
    year,
    startYear: undefined,
    tradeDirection: TradeDirection.export,
    productClass: ProductClass.HS,
    nodeSizing: NodeSizing.None,
    tradeFlow: TradeFlow.Gross,
    target: Target.Product,
    partner: undefined,
  };

  const anchorProps ={ href: `/explore/treemap?product=product-HS92-${productIDString}&view=markets&exporter=group-1` }

  return (
    <React.Fragment>
      <ProductNameOuterContainer style={productNameStyle}>
        <ProductLink href={anchorProps.href} target='_blank'>
          <ProductName>
            {name}
          </ProductName>
          <ProductCode>{productCode}</ProductCode>
          <NewTabArrow
            dangerouslySetInnerHTML={{__html: require('./diagonal-right-arrow.svg')}}
          />
        </ProductLink>
      </ProductNameOuterContainer>
      <DataCell>
        <StarRating decile={feasibilityDecile}/>
      </DataCell>
      <DataCell>
        <StarRating decile={opportunityGainDecile}/>
      </DataCell>
      <DataCell>
        <StarRating decile={complexityDecile}/>
      </DataCell>
      <DataCell>
        {formatTradeValue(globalExportValue)}
      </DataCell>
      <DataCell>
        <Arrow
          color={arrowColor}
          height={20}
          dangerouslySetInnerHTML={{__html: require('../sidebar/arrow.svg')}}
        />
        <ExportValueChangeContainer>
          {formatChange(globalExportValueChangeMagnitude)}
        </ExportValueChangeContainer>
      </DataCell>
    </React.Fragment>
  );
};

export interface TableDatum {
  id: string;
  color: string;
  name: string;
  code: string;
  complexityDecile: DecileClassification;
  opportunityGainDecile: DecileClassification;
  feasibilityDecile: DecileClassification;
  globalExportValue: number;
  globalExportValueChange: number;
  normalizedPCI: number;
  normalizedFeasibility: number;
  normalizedOpportunityGain: number;
  score: number;
}

interface Props {
  products: TableDatum[];
  getFluentString: GetString;
  width: number;
  height: number;
  zIndexFromParent: number;
  year: number;
}
const Table = (props: Props) => {
  const {
    products, getFluentString, width,
    height, zIndexFromParent, year,
  } = props;
  const [sortCriterion, setSortCriterion] = useState<SortCriterion>(SortCriterion.Complexity);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.Descending);

  const {
    promoteZIndex, restoreZIndex, getZIndex,
  } = usePromotableZIndex({
    normalZIndices: zIndices,
    promotedZIndex: promotedTitleCellZIndex,
  });

  const getScore = (product: TableDatum) => product.score;
  let productsSortedAscendingly: typeof products;
  if (sortCriterion === SortCriterion.Complexity) {
    productsSortedAscendingly = sortBy(products, [product => product.normalizedPCI, getScore]);
  } else if (sortCriterion === SortCriterion.OpportunityGain) {
    productsSortedAscendingly = sortBy(products, [product => product.normalizedOpportunityGain, getScore]);
  } else if (sortCriterion === SortCriterion.Feasibility) {
    productsSortedAscendingly = sortBy(products, [product => product.normalizedFeasibility, getScore]);
  } else if (sortCriterion === SortCriterion.ExportValue) {
    productsSortedAscendingly = sortBy(products, [product => product.globalExportValue, getScore]);
  } else if (sortCriterion === SortCriterion.ExportValueChange) {
    productsSortedAscendingly = sortBy(products, [product => product.globalExportValueChange, getScore]);
  } else {
    failIfValidOrNonExhaustive(sortCriterion, 'Invalid sort criterion ' + sortCriterion);
    productsSortedAscendingly = products;
  }

  let sortedProducts: typeof products;
  if (sortDirection === SortDirection.Ascending) {
    sortedProducts = productsSortedAscendingly;
  } else if (sortDirection === SortDirection.Descending) {
    sortedProducts = productsSortedAscendingly.reverse();
  } else {
    failIfValidOrNonExhaustive(sortDirection, 'Invalid sort direction' + sortDirection);
    sortedProducts = productsSortedAscendingly;
  }

  const onColumnTitleClick = (nextCriterion: SortCriterion) => {
    const prevCriterion = sortCriterion;
    if (prevCriterion !== nextCriterion) {
      setSortCriterion(nextCriterion);
      setSortDirection(SortDirection.Descending);
    } else {
      // If same criterion is requested, flip sort direction:
      const prevDirection = sortDirection;
      let newDirection: SortDirection;
      if (prevDirection === SortDirection.Ascending) {
        newDirection = SortDirection.Descending;
      } else if (prevDirection === SortDirection.Descending) {
        newDirection = SortDirection.Ascending;
      } else {
        failIfValidOrNonExhaustive(prevDirection, 'Invalid sort direction ' + prevDirection);
        newDirection = SortDirection.Ascending;
      }
      setSortDirection(newDirection);
    }
  };

  const distanceTooltip = (
      <DistanceLegendContainer>
        <DistanceLegend>
          <Diamond fill={Fill.InvertedFull} />
          <Diamond fill={Fill.InvertedFull} />
          <Diamond fill={Fill.InvertedFull} />
          <Diamond fill={Fill.InvertedFull} />
          <Diamond fill={Fill.InvertedFull} />
          <LegendLabel>{getFluentString('glossary-distance-proximity-legend-most-nearby')}</LegendLabel>
        </DistanceLegend>
        <DistanceLegend>
          <Diamond fill={Fill.InvertedNone} />
          <Diamond fill={Fill.InvertedNone} />
          <Diamond fill={Fill.InvertedNone} />
          <Diamond fill={Fill.InvertedNone} />
          <Diamond fill={Fill.InvertedNone} />
          <LegendLabel>{getFluentString('glossary-distance-proximity-legend-most-distant')}</LegendLabel>
        </DistanceLegend>
      </DistanceLegendContainer>
  );

  const titleRow = (
    <>
      <TitleCell
        hasAssignedSortCriterion={false}
        zIndex={getZIndex('tableCellProductName')}
        stickColumnToLeft={true}
      >
        {getFluentString('high-potential-products-table-name-column-title')}
      </TitleCell>
      <TitleCell
        hasAssignedSortCriterion={true}
        assignedSortCriterion={SortCriterion.Feasibility}
        onClick={onColumnTitleClick}
        currentSortCriterion={sortCriterion}
        currentSortDirection={sortDirection}
        zIndex={getZIndex('tableCellFeasibility')}
        promoteZIndex={() => promoteZIndex('tableCellFeasibility')}
        restoreZIndex={restoreZIndex}
      >
        <div>
          <GlossaryTerm
            type={TooltipType.Standard}
            term={getFluentString('high-potential-products-table-feasibility-column-title')}
            explanation={getFluentString('glossary-distance-proximity-term-definition')}
            citation={undefined}
            align={TooltipAlign.Standard}
            attachment={distanceTooltip}
          />
        </div>
      </TitleCell>
      <TitleCell
        hasAssignedSortCriterion={true}
        assignedSortCriterion={SortCriterion.OpportunityGain}
        onClick={onColumnTitleClick}
        currentSortCriterion={sortCriterion}
        currentSortDirection={sortDirection}
        zIndex={getZIndex('tableCellOpportunityGain')}
        promoteZIndex={() => promoteZIndex('tableCellOpportunityGain')}
        restoreZIndex={restoreZIndex}
      >
        <div>
          <GlossaryTerm
            type={TooltipType.Standard}
            term={getFluentString('high-potential-products-table-opportunity-gain-column-title')}
            explanation={getFluentString('glossary-opportunity-gain-term-definition')}
            citation={undefined}
            align={TooltipAlign.Standard}
            attachment={null}
          />
        </div>
      </TitleCell>
      <TitleCell
        hasAssignedSortCriterion={true}
        assignedSortCriterion={SortCriterion.Complexity}
        currentSortCriterion={sortCriterion}
        currentSortDirection={sortDirection}
        onClick={onColumnTitleClick}
        zIndex={getZIndex('tableCellComplexity')}
        promoteZIndex={() => promoteZIndex('tableCellComplexity')}
        restoreZIndex={restoreZIndex}
      >
        <div>
          <GlossaryTerm
            type={TooltipType.Standard}
            term={getFluentString('high-potential-products-table-complexity-column-title')}
            explanation={getFluentString('glossary-product-complexity-term-definition')}
            citation={undefined}
            align={TooltipAlign.Standard}
            attachment={null}
          />
        </div>
      </TitleCell>
      <TitleCell
        hasAssignedSortCriterion={true}
        assignedSortCriterion={SortCriterion.ExportValue}
        onClick={onColumnTitleClick}
        currentSortCriterion={sortCriterion}
        currentSortDirection={sortDirection}
        zIndex={getZIndex('tableCellExportValue')}
        promoteZIndex={() => promoteZIndex('tableCellExportValue')}
        restoreZIndex={restoreZIndex}
      >
        {getFluentString('high-potential-products-table-trade-column-title')}
      </TitleCell>
      <TitleCell
        hasAssignedSortCriterion={true}
        assignedSortCriterion={SortCriterion.ExportValueChange}
        onClick={onColumnTitleClick}
        currentSortCriterion={sortCriterion}
        currentSortDirection={sortDirection}
        zIndex={getZIndex('tableCellExportValueChange')}
        promoteZIndex={() => promoteZIndex('tableCellExportValueChange')}
        restoreZIndex={restoreZIndex}
      >
        {getFluentString('high-potential-products-table-trade-change-column-title')}
      </TitleCell>
    </>
  );

  const contentRows = sortedProducts.map(product => (
    <TableRow product={product} year={year} key={product.id}/>
  ));
  return (
    <>
      <Root
        style={{width, height, zIndex: zIndexFromParent}}
      >
        {titleRow}
        {contentRows}
      </Root>
    </>
  );
};

export default Table;
